import { useState } from "react";
import { AlertTriangle, ArrowLeft, CircleAlert, Lock, Pin, Plus, Save, Trash2, X } from "lucide-react";
import type { MailReward } from "@/lib/api";
import { NEWS_LANGUAGES, type NewsIssue, type NewsLanguage } from "@/lib/news-schema";
import { IAP_CATALOG, IAP_REWARD_TYPE } from "@/lib/iap-rewards";
import { HERO_NAMES, REWARD_TYPE_NAMES } from "@/types/cloud-data";
import { cn } from "@/lib/utils";
import { ItemPicker } from "@/components/ItemPicker";
import { RewardIcon } from "@/components/players/PlayerInspector";
import { ProductArt } from "@/components/purchases/ProductArt";
import { DateTimeField } from "./DateTimeField";
import { REWARD_FIELDS, type Draft } from "./news-model";
import { MarkupText } from "@/components/trial/MarkupText";

function IapSelect({ value, onChange, n }: { value: string; onChange: (id: string) => void; n: number }) {
  const [search, setSearch] = useState("");
  const selected = IAP_CATALOG.find(p => p.id === value);
  const matches = IAP_CATALOG.filter(p => p.id === value || `${p.id} ${p.aliases.join(" ")} ${p.summary}`.toLowerCase().includes(search.toLowerCase()));
  return <span className="nw-iap">
    <input aria-label={`Reward ${n} product search`} placeholder="Search products" value={search} onChange={e => setSearch(e.target.value)} />
    <select aria-label={`Reward ${n} product`} required value={value} onChange={e => onChange(e.target.value)}>
      <option value="" disabled>Product</option>
      {value && !selected && <option value={value} disabled>{value} — unknown product</option>}
      {matches.map(p => <option key={p.id} value={p.id} disabled={!!p.unavailable}>{p.id}{p.unavailable ? " — unavailable: " + p.unavailable : " — " + p.summary}</option>)}
    </select>
    {selected && <small>{selected.summary}</small>}
  </span>;
}

function RewardRow({ reward, n, onChange, onRemove }: { reward: MailReward; n: number; onChange: (next: MailReward) => void; onRemove: () => void }) {
  const f = REWARD_FIELDS[reward.rewardType] ?? {};
  return <li className="nw-reward">
    <span className="nw-reward-icon">{reward.rewardType === IAP_REWARD_TYPE && reward.iapProductId ? <ProductArt productId={reward.iapProductId} className="nw-art" /> : <RewardIcon reward={reward} />}</span>
    <select className="nw-reward-type" aria-label={`Reward ${n} type`} value={reward.rewardType} onChange={e => onChange({
      rewardType: parseInt(e.target.value), amount: 1, itemID: undefined, hero: undefined, developerBuffID: undefined, recipe: undefined, iapProductId: undefined })}>
      {Object.entries(REWARD_TYPE_NAMES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
    </select>
    <span className="nw-reward-detail">
      {f.iap && <IapSelect n={n} value={reward.iapProductId ?? ""} onChange={iapProductId => onChange({ ...reward, iapProductId })} />}
      {f.item && <ItemPicker className="nw-item-picker" value={reward.itemID ?? ""} onChange={itemID => onChange({ ...reward, itemID })} placeholder="Search items" />}
      {f.hero && <select aria-label={`Reward ${n} hero`} value={reward.hero ?? ""} onChange={e => onChange({ ...reward, hero: parseInt(e.target.value) })}>
        <option value="" disabled>Hero</option>
        {Object.entries(HERO_NAMES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>}
      {f.buff && <input aria-label={`Reward ${n} buff ID`} placeholder="Buff ID" value={reward.developerBuffID ?? ""} onChange={e => onChange({ ...reward, developerBuffID: e.target.value })} />}
      {f.recipe && <input aria-label={`Reward ${n} recipe ID`} placeholder="Recipe ID" type="number" value={reward.recipe ?? ""} onChange={e => onChange({ ...reward, recipe: parseInt(e.target.value) || 0 })} />}
    </span>
    {f.amount ? <input className="nw-reward-amount" type="number" aria-label={`Reward ${n} ${f.iap ? "packs" : "amount"}`} title={f.iap ? "Packs" : "Amount"} placeholder={f.iap ? "Packs" : "Amount"}
      value={reward.amount} onChange={e => onChange({ ...reward, amount: parseInt(e.target.value) || 0 })} /> : <span className="nw-reward-amount" />}
    <button type="button" className="pw-icon-button nw-reward-remove" aria-label={`Remove reward ${n}`} onClick={onRemove}><X size={15} /></button>
  </li>;
}

function IssueRow({ issue }: { issue: NewsIssue }) {
  const error = issue.severity === "error";
  return <li className={cn("nw-issue", error ? "is-error" : "is-warn")}>
    {error ? <CircleAlert size={14} /> : <AlertTriangle size={14} />}
    <span>{issue.path && <code>{issue.path}</code>}{issue.message}</span>
  </li>;
}

export function NewsEditor({ draft, setDraft, isNew, errors, warnings, canSave, onSave, saving, onDelete, deleting, readOnly, onBack }: {
  draft: Draft; setDraft: (d: Draft) => void; isNew: boolean; errors: NewsIssue[]; warnings: NewsIssue[]; canSave: boolean;
  onSave: () => void; saving: boolean; onDelete?: () => void; deleting: boolean; readOnly: boolean; onBack: () => void;
}) {
  const [lang, setLang] = useState<NewsLanguage>("en");
  const [previewing, setPreviewing] = useState(false);
  const update = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const setReward = (i: number, next: MailReward) => update({ rewards: draft.rewards.map((r, j) => j === i ? next : r) });

  return <div className="nw-editor">
    <div className="nw-editor-main">
      <section className="ws-card ws-form nw-compose">
        <div className="nw-id-row">
          <button type="button" className="pw-icon-button nw-back" aria-label="All posts" onClick={onBack}><ArrowLeft size={17} /></button>
          <span className={cn("nw-id", !isNew && "is-locked")} title={isNew ? undefined : "Locked: it is the read-state key. A new id marks the post unread for every player."}>
            {!isNew && <Lock size={12} />}
            <input aria-label="Post id" className="ws-mono" value={draft.id} disabled={!isNew} placeholder="2026-08-patch-1-9-0" onChange={e => update({ id: e.target.value })} />
          </span>
          <select className="nw-lang" aria-label="Language" value={lang} onChange={e => setLang(e.target.value as NewsLanguage)}>
            {NEWS_LANGUAGES.map(l => <option key={l} value={l}>{l.toUpperCase()}{l === "en" && " (required)"}</option>)}
          </select>
          <div className="ws-segmented nw-mode" role="group" aria-label="View">{([false, true] as const).map(p => <button key={String(p)} type="button"
            className={cn(previewing === p && "is-active")} aria-pressed={previewing === p} onClick={() => setPreviewing(p)}>{p ? "Preview" : "Edit"}</button>)}</div>
        </div>
        {previewing ? <section className="nw-preview" aria-label="Preview">
          <strong><MarkupText text={draft.titles[lang] || draft.titles.en || "(no title)"} /></strong>
          <p><MarkupText text={draft.bodies[lang] || draft.bodies.en || "(no body)"} /></p>
        </section> : <>
        <input className="nw-title" aria-label={`Title (${lang})`} placeholder={`Title (${lang.toUpperCase()})`} value={draft.titles[lang] ?? ""} onChange={e => update({ titles: { ...draft.titles, [lang]: e.target.value } })} />
        <textarea className="nw-body" aria-label={`Body (${lang})`} placeholder={`Body (${lang.toUpperCase()})`} rows={10} value={draft.bodies[lang] ?? ""} onChange={e => update({ bodies: { ...draft.bodies, [lang]: e.target.value } })} />
        </>}
        <div className="nw-schedule">
          <DateTimeField label="Publish at" placeholder="Publish: immediately" value={draft.publishAt} onChange={publishAt => update({ publishAt })} />
          <DateTimeField label="Expire at" placeholder="Expire: never" value={draft.expireAt} onChange={expireAt => update({ expireAt })} />
        </div>
        <div className="nw-status-row">
          <div className="ws-segmented" role="group" aria-label="Status">{([true, false] as const).map(isDraft => <button key={String(isDraft)} type="button"
            className={cn(draft.draft === isDraft && "is-active")} aria-pressed={draft.draft === isDraft} onClick={() => update({ draft: isDraft })}>{isDraft ? "Draft" : "Published"}</button>)}</div>
          <label className="nw-pin"><input type="checkbox" checked={draft.pinned} onChange={e => update({ pinned: e.target.checked })} /><Pin size={13} />Pinned</label>
        </div>
      </section>

      <section className="ws-card ws-form">
        <div className="ws-card-head"><h2>Rewards</h2><span className="ws-muted">Every player can claim once</span></div>
        {draft.rewards.length > 0 && <ol className="nw-rewards">{draft.rewards.map((r, i) => <RewardRow key={i} n={i + 1} reward={r} onChange={next => setReward(i, next)}
          onRemove={() => update({ rewards: draft.rewards.filter((_, j) => j !== i) })} />)}</ol>}
        <button type="button" className="pw-button nw-add" onClick={() => update({ rewards: [...draft.rewards, { rewardType: 2, amount: 100 }] })}><Plus size={14} />Add reward</button>
      </section>

      {errors.length + warnings.length > 0 && <ul className="nw-issues" aria-label="Issues">{errors.map((issue, i) => <IssueRow key={`e${i}`} issue={issue} />)}{warnings.map((issue, i) => <IssueRow key={`w${i}`} issue={issue} />)}</ul>}

      <div className="nw-actions">
        {onDelete && <button type="button" className="pw-button pw-button-danger" onClick={onDelete} disabled={deleting || readOnly}><Trash2 size={14} />Delete</button>}
        <button type="button" className="pw-button pw-button-primary" onClick={onSave} disabled={!canSave || saving || deleting || readOnly}>
          <Save size={14} />{saving ? "Saving…" : draft.draft ? "Save draft" : "Save & publish"}
        </button>
      </div>
    </div>

  </div>;
}
